import React from "react";

/**
 * Skeleton loading component
 *
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.width - Width of the skeleton (e.g., '100%', '200px')
 * @param {string} props.height - Height of the skeleton (e.g., '24px', '100%')
 * @param {string} props.borderRadius - Border radius of the skeleton
 * @param {boolean} props.animated - Whether to animate the skeleton
 * @param {string} props.variant - Variant of the skeleton (default, circle, text)
 * @returns {JSX.Element}
 */
function Skeleton({
  className = "",
  width,
  height,
  borderRadius = "0.25rem",
  animated = true,
  variant = "default",
}) {
  const baseClasses = "bg-gray-200 dark:bg-gray-700";

  // Animation classes
  const animationClasses = animated ? "animate-pulse" : "";

  // Variant-specific classes
  let variantClasses = "";
  if (variant === "circle") {
    borderRadius = "50%";
  } else if (variant === "text") {
    variantClasses = "leading-none";
  }

  const style = {
    width: width || "100%",
    height: height || "1rem",
    borderRadius,
  };

  return (
    <div
      className={`${baseClasses} ${animationClasses} ${variantClasses} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
}

/**
 * Text skeleton component
 */
Skeleton.Text = function SkeletonText({
  lines = 1,
  className = "",
  lineHeight = "1rem",
  spacing = "0.5rem",
  ...props
}) {
  return (
    <div className={`space-y-${spacing} ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton key={index} variant="text" height={lineHeight} {...props} />
      ))}
    </div>
  );
};

/**
 * Circle skeleton component
 */
Skeleton.Circle = function SkeletonCircle({ size = "2.5rem", ...props }) {
  return <Skeleton variant="circle" width={size} height={size} {...props} />;
};

/**
 * Card skeleton component
 */
Skeleton.Card = function SkeletonCard({ header = true, rows = 3, ...props }) {
  return (
    <div className="border rounded-lg p-4 space-y-4" {...props}>
      {header && (
        <div className="flex justify-between items-center mb-4">
          <Skeleton width="40%" height="1.5rem" />
          <Skeleton width="20%" height="1rem" />
        </div>
      )}
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton key={index} height="1rem" />
        ))}
      </div>
    </div>
  );
};

/**
 * Table skeleton component
 */
Skeleton.Table = function SkeletonTable({ rows = 5, columns = 4, ...props }) {
  return (
    <div className="w-full" {...props}>
      {/* Header */}
      <div className="flex border-b pb-2 mb-2">
        {Array.from({ length: columns }).map((_, index) => (
          <div key={index} className="flex-1 px-2">
            <Skeleton width="80%" height="1.25rem" />
          </div>
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex py-3 border-b last:border-0">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div key={colIndex} className="flex-1 px-2">
              <Skeleton width={colIndex === 0 ? "70%" : "80%"} height="1rem" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * Loader skeleton component (e.g., for full-page or section loading)
 */
Skeleton.Loader = function SkeletonLoader({
  size = "6rem",
  className = "",
  ...props
}) {
  return (
    <div
      {...props}
      className={`
        fixed inset-0 flex items-center justify-center ${className}
      `}
      aria-label="Loading"
    >
      <div
        className="
          animate-spin
          rounded-full
          border-8
          border-gray-300
          border-t-blue-500
          dark:border-gray-700
          dark:border-t-blue-400
        "
        style={{
          width: size,
          height: size,
        }}
      />
    </div>
  );
};

export default Skeleton;
